import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { ArrowLeft, Check, HelpCircle, X, Camera, Baby, User as UserIcon } from 'lucide-react';
import { API_URL } from '../utils/api';
import ShareButton from '../components/ShareButton';

const VERDICT_META = {
  GO: { Icon: Check, accent: 'text-qc-green', bg: 'bg-qc-green/10', border: 'border-qc-green/30' },
  MAYBE: { Icon: HelpCircle, accent: 'text-qc-amber', bg: 'bg-qc-amber/10', border: 'border-qc-amber/30' },
  'HELL NO': { Icon: X, accent: 'text-qc-red', bg: 'bg-qc-red/10', border: 'border-qc-red/30' },
};

/**
 * MenuResult — displays the whole-table verdict grid:
 *   rows = menu items
 *   for each item: one row per selected profile with their verdict
 * Tap an item to expand its reasons for each profile.
 */
export default function MenuResult() {
  const { scanId } = useParams();
  const [menu, setMenu] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API_URL}/scan/menu/${scanId}`, {
          withCredentials: true,
        });
        setMenu(r.data);
        if (navigator.vibrate) navigator.vibrate([40, 60, 40]);
      } catch (e) {
        console.error('[MenuResult] Failed to load menu scan:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [scanId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-qc-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-qc-green border-t-transparent" />
      </div>
    );
  }

  if (!menu) {
    return (
      <div className="min-h-screen bg-qc-bg flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-qc-text mb-4 font-bold">Menu scan not found</p>
          <Button onClick={() => navigate('/home')} className="bg-qc-green text-qc-bg rounded-full px-6 font-extrabold">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  // Precompute the summary tally across all profiles + items
  const tally = { GO: 0, MAYBE: 0, 'HELL NO': 0 };
  for (const item of menu.items) {
    for (const v of item.verdicts) {
      tally[v.decision] = (tally[v.decision] || 0) + 1;
    }
  }

  return (
    <div className="min-h-screen bg-qc-bg text-qc-text pb-32" data-testid="menu-result-screen"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
    >
      {/* Header */}
      <div className="px-5 py-3 flex items-center max-w-lg mx-auto">
        <button
          onClick={() => navigate('/home')}
          data-testid="back-button"
          className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text tappable"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-base font-extrabold text-qc-text ml-3">Table verdicts</h1>
      </div>

      <div className="max-w-lg mx-auto px-5 pt-3">
        {/* Summary tally */}
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="menu-tally">
          {['GO', 'MAYBE', 'HELL NO'].map((d) => {
            const meta = VERDICT_META[d];
            const Icon = meta.Icon;
            return (
              <div key={d} className={`rounded-2xl p-3 border ${meta.border} ${meta.bg} text-center`}>
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Icon className={`w-4 h-4 ${meta.accent}`} strokeWidth={3} />
                  <span className={`text-[10px] font-black uppercase tracking-wider ${meta.accent}`}>{d}</span>
                </div>
                <div className={`text-2xl font-black tabular-nums ${meta.accent}`} data-testid={`menu-tally-${d.replace(' ', '-').toLowerCase()}`}>
                  {tally[d]}
                </div>
              </div>
            );
          })}
        </div>

        {/* Items */}
        <div className="space-y-3" data-testid="menu-items-list">
          {menu.items.map((item, idx) => {
            const isOpen = expanded === item.item_name;
            const itemKey = `${item.item_name}-${idx}`;
            return (
              <div key={itemKey} className="bg-qc-surface border border-qc-border rounded-2xl overflow-hidden" data-testid={`menu-item-${idx}`}>
                <button
                  onClick={() => setExpanded(isOpen ? null : item.item_name)}
                  className="w-full text-left px-4 py-3 tappable"
                  data-testid={`menu-item-toggle-${idx}`}
                >
                  <p className="text-base font-extrabold text-qc-text truncate">{item.item_name}</p>
                  {item.description && (
                    <p className="text-xs text-qc-text-dim mt-0.5 truncate">{item.description}</p>
                  )}
                  {/* Per-profile verdict chip row */}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {item.verdicts.map((v) => {
                      const meta = VERDICT_META[v.decision] || VERDICT_META.MAYBE;
                      const Icon = meta.Icon;
                      const ProfIcon = v.is_kid ? Baby : UserIcon;
                      return (
                        <span
                          key={v.profile_id}
                          data-testid={`menu-verdict-chip-${idx}-${v.profile_id}`}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-bold ${meta.border} ${meta.bg} ${meta.accent}`}
                        >
                          <ProfIcon className="w-3 h-3" />
                          <span className="max-w-[70px] truncate">{v.profile_name || 'You'}</span>
                          <Icon className="w-3 h-3" strokeWidth={3} />
                        </span>
                      );
                    })}
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-qc-border px-4 py-3 space-y-3 bg-qc-bg/40" data-testid={`menu-item-expanded-${idx}`}>
                    {item.verdicts.map((v) => {
                      const meta = VERDICT_META[v.decision] || VERDICT_META.MAYBE;
                      const Icon = meta.Icon;
                      return (
                        <div key={v.profile_id} className={`rounded-xl border ${meta.border} ${meta.bg} p-3`}>
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${meta.bg} ${meta.accent}`}>
                              <Icon className="w-3.5 h-3.5" strokeWidth={3} />
                            </div>
                            <span className={`text-xs font-black uppercase tracking-wider ${meta.accent}`}>{v.decision}</span>
                            <span className="text-sm font-bold text-qc-text truncate">for {v.profile_name || 'You'}</span>
                          </div>
                          {v.reasons.length > 0 && (
                            <ul className="space-y-1">
                              {v.reasons.map((r, i) => (
                                <li key={`${v.profile_id}-r-${i}`} className="flex items-start gap-2 text-sm text-qc-text leading-snug">
                                  <div className={`w-1 h-1 rounded-full mt-2 flex-shrink-0 ${meta.accent.replace('text-', 'bg-')}`} />
                                  {r}
                                </li>
                              ))}
                            </ul>
                          )}
                          {v.suggestion && (
                            <p className="text-xs text-qc-text-dim mt-2 italic">💡 {v.suggestion}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Fixed bottom action */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-qc-bg via-qc-bg/95 to-transparent pt-6 pb-2 px-5 z-40"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Button
            onClick={() => navigate('/menu-scan')}
            data-testid="menu-scan-again"
            className="capture-btn flex-1 h-14 bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-2xl font-black text-base tracking-tight shadow-[0_10px_40px_rgba(0,230,118,0.35)] flex items-center justify-center gap-3"
          >
            <Camera className="w-5 h-5" strokeWidth={2.5} />
            Scan another menu
          </Button>
          <ShareButton
            kind="menu"
            payload={{ items: menu.items }}
            testId="menu-share-button"
            compact
          />
        </div>
      </div>
    </div>
  );
}
